import { ChartingView } from './ChartingViewToggle.component'

/** Design-time tooth slot width (matches SVG viewBox width). */
export const TOOTH_DESIGN_WIDTH = 80
export const TOOTH_COUNT = 16
export const TOOTH_ROW_GAP = 8
export const CHART_ROW_GAP = 18
/** Legacy chart column width — caps full-width layout (~56px teeth at 80px SVG scale). */
export const CHART_REFERENCE_WIDTH = 1016
/** Upper bound used when fitting tooth width to available viewport height. */
export const CHART_FULL_WIDTH_MAX_HEIGHT_VH = 0.48
export const CHART_FULL_WIDTH_MAX_HEIGHT_PX = 580

export const getFullWidthChartMaxHeight = (): number => {
  if (typeof window === 'undefined') {
    return CHART_FULL_WIDTH_MAX_HEIGHT_PX
  }
  return Math.min(
    window.innerHeight * CHART_FULL_WIDTH_MAX_HEIGHT_VH,
    CHART_FULL_WIDTH_MAX_HEIGHT_PX,
  )
}
/** Legacy layout width used for perio overlay positions before fluid sizing. */
export const LEGACY_TOOTH_WIDTH = 56

/** Fixed label size (matches Tooth.css). */
export const TOOTH_NUMBER_FONT_PX = 14
export const TOOTH_NUMBER_LINE_HEIGHT_PX = 20
/** Padding on the chart-border side of the number (above upper / below lower). */
export const TOOTH_NUMBER_OUTER_PADDING_Y_PX = 12
/** Padding on the tooth side of the number. */
export const TOOTH_NUMBER_INNER_PADDING_Y_PX = 5
/**
 * Buccal base padding for sinus_tract SVG translate(0 ±20).
 * Applied as padding-top on upper bases / padding-bottom on lower bases.
 */
export const SINUS_TRACT_GAP_PX = 20
export const TOOTH_NUMBER_BLOCK_PX =
  TOOTH_NUMBER_LINE_HEIGHT_PX +
  TOOTH_NUMBER_OUTER_PADDING_Y_PX +
  TOOTH_NUMBER_INNER_PADDING_Y_PX
/** Ratios aligned with Tooth.css (`--tooth-width` slot sizing). */
const PART_GAP_RATIO = 10 / TOOTH_DESIGN_WIDTH
const BUCCAL_HEIGHT_RATIO = 200 / TOOTH_DESIGN_WIDTH
const CROWN_HEIGHT_RATIO = 80 / TOOTH_DESIGN_WIDTH
const PALATAL_LINGUAL_TOTAL_RATIO = (170 + 15) / LEGACY_TOOTH_WIDTH

const partGap = (toothWidth: number): number => toothWidth * PART_GAP_RATIO
const buccalHeight = (toothWidth: number): number =>
  toothWidth * BUCCAL_HEIGHT_RATIO
const crownHeight = (toothWidth: number): number =>
  toothWidth * CROWN_HEIGHT_RATIO
const palatalLingualHeight = (toothWidth: number): number =>
  toothWidth * PALATAL_LINGUAL_TOTAL_RATIO

const numberBlockHeight = (
  _toothWidth: number,
  showToothNumber: boolean,
): number => (showToothNumber ? TOOTH_NUMBER_BLOCK_PX : 0)

/** Flex gap between the tooth number and the buccal base (tooth-card gap). */
const numberToBaseGap = (
  toothWidth: number,
  showToothNumber: boolean,
): number => (showToothNumber ? partGap(toothWidth) : 0)

/** Buccal SVG + apex-side padding for sinus_tract translate(0 ±20). */
const buccalWithSinusHeight = (toothWidth: number): number =>
  buccalHeight(toothWidth) + SINUS_TRACT_GAP_PX

/**
 * Full view row: number | base | crown (or crown | base | number).
 * Two flex gaps when the number is shown; one gap when it is hidden.
 */
const singleRowHeightFull = (
  toothWidth: number,
  showToothNumber: boolean,
): number => {
  return (
    numberBlockHeight(toothWidth, showToothNumber) +
    numberToBaseGap(toothWidth, showToothNumber) +
    buccalWithSinusHeight(toothWidth) +
    partGap(toothWidth) +
    crownHeight(toothWidth)
  )
}

/** Upper view: number | base | crown | palatal */
const singleRowHeightUpper = (
  toothWidth: number,
  showToothNumber: boolean,
): number => {
  return (
    numberBlockHeight(toothWidth, showToothNumber) +
    numberToBaseGap(toothWidth, showToothNumber) +
    buccalWithSinusHeight(toothWidth) +
    partGap(toothWidth) +
    crownHeight(toothWidth) +
    partGap(toothWidth) +
    palatalLingualHeight(toothWidth)
  )
}

/** Lower view: lingual | crown | base | number */
const singleRowHeightLower = (
  toothWidth: number,
  showToothNumber: boolean,
): number => {
  return (
    palatalLingualHeight(toothWidth) +
    partGap(toothWidth) +
    crownHeight(toothWidth) +
    partGap(toothWidth) +
    buccalWithSinusHeight(toothWidth) +
    numberToBaseGap(toothWidth, showToothNumber) +
    numberBlockHeight(toothWidth, showToothNumber)
  )
}

/**
 * Tooth-scaled row height (fixed number label px not included).
 * Includes the number↔base flex gap when numbers are shown — that gap scales
 * with --tooth-width.
 */
const scaledRowHeight = (
  toothWidth: number,
  chartView: ChartingView,
  showToothNumber = true,
): number => {
  const numberGap = numberToBaseGap(toothWidth, showToothNumber)
  if (chartView === 'full' || chartView === 'upper') {
    let height =
      numberGap +
      buccalHeight(toothWidth) +
      partGap(toothWidth) +
      crownHeight(toothWidth)
    if (chartView === 'upper') {
      height += partGap(toothWidth) + palatalLingualHeight(toothWidth)
    }
    return height
  }
  return (
    palatalLingualHeight(toothWidth) +
    partGap(toothWidth) +
    crownHeight(toothWidth) +
    partGap(toothWidth) +
    buccalHeight(toothWidth) +
    numberGap
  )
}

const scaledChartHeightPerToothWidth = (
  chartView: ChartingView,
  showToothNumber = true,
): number => {
  if (chartView === 'full') {
    return scaledRowHeight(1, 'full', showToothNumber) * 2
  }
  return scaledRowHeight(1, chartView, showToothNumber)
}

const fixedNumberBlockHeight = (
  chartView: ChartingView,
  showToothNumber: boolean,
): number => {
  if (!showToothNumber) {
    return 0
  }
  const rowCount = chartView === 'full' ? 2 : 1
  return rowCount * TOOTH_NUMBER_BLOCK_PX
}

/** One sinus-tract padding per jaw row that includes a buccal base. */
const fixedSinusPaddingHeight = (chartView: ChartingView): number => {
  const rowCount = chartView === 'full' ? 2 : 1
  return rowCount * SINUS_TRACT_GAP_PX
}

export const computeToothWidthFromContainer = (
  containerWidth: number,
): number => {
  if (containerWidth <= 0) {
    return TOOTH_DESIGN_WIDTH
  }
  const gaps = (TOOTH_COUNT - 1) * TOOTH_ROW_GAP
  return Math.min(TOOTH_DESIGN_WIDTH, (containerWidth - gaps) / TOOTH_COUNT)
}

export const computeToothWidthFromHeight = (
  containerHeight: number,
  chartView: ChartingView,
  showToothNumber = true,
): number => {
  if (containerHeight <= 0) {
    return TOOTH_DESIGN_WIDTH
  }
  const rowGap = chartView === 'full' ? CHART_ROW_GAP : 0
  const numberBlock = fixedNumberBlockHeight(chartView, showToothNumber)
  const sinusPadding = fixedSinusPaddingHeight(chartView)
  const availableHeight = Math.max(
    0,
    containerHeight - rowGap - numberBlock - sinusPadding,
  )
  const heightPerToothWidth = scaledChartHeightPerToothWidth(
    chartView,
    showToothNumber,
  )
  if (heightPerToothWidth <= 0) {
    return TOOTH_DESIGN_WIDTH
  }
  return Math.min(TOOTH_DESIGN_WIDTH, availableHeight / heightPerToothWidth)
}

export const computeChartHeight = (
  toothWidth: number,
  chartView: ChartingView,
  showToothNumber = true,
): number => {
  if (chartView === 'full') {
    return singleRowHeightFull(toothWidth, showToothNumber) * 2 + CHART_ROW_GAP
  }
  if (chartView === 'upper') {
    return singleRowHeightUpper(toothWidth, showToothNumber)
  }
  return singleRowHeightLower(toothWidth, showToothNumber)
}

export const computeToothWidth = (params: {
  containerWidth: number
  containerHeight?: number
  view: ChartingView
  allowHeightFit?: boolean
  showToothNumber?: boolean
}): number => {
  const showToothNumber = params.showToothNumber ?? true
  const sizingWidth = params.allowHeightFit
    ? params.containerWidth
    : Math.min(params.containerWidth, CHART_REFERENCE_WIDTH)

  const widthFit = computeToothWidthFromContainer(sizingWidth)

  if (!params.containerHeight || params.containerHeight <= 0) {
    return widthFit
  }

  const heightFit = computeToothWidthFromHeight(
    params.containerHeight,
    params.view,
    showToothNumber,
  )

  return Math.min(widthFit, heightFit)
}
